var _ = require('lodash'),
  generalUtility = require('./generalUtility'),
  log = require('../libraries/logger').getLoggerInstance('Base.HTMLUtility'),
  RSVP = require('rsvp');

var publicMethods = {
  /**
   * Get the value from an element. Depending on the element type will depend what is returned. When an input is passed in the .value attribute is returned.
   * When a element like a DIV is passed in, the TextContent will be returned.
   */
  val: function (element) {
    var value;

    if (!element) {
      throw new Error('Invalid element selection');
    }

    if('value' in element){
      // Handles input fields
      value = element.value;
    } else if('textContent' in element){
      // Handles display elements like div elements
      value = element.textContent;
    }

    return value;
  },
  /**
   * Execute a function when an event is triggered on a dom element
   * @param {string} event - The trigger event
   * @param {Object} element - The element to bind the event trigger to
   * @param {function} callback - The function to call when the event is triggered
   * @param {function} callbackCallback - An optional function that will be passed into the callback funciton. This can be used to execute another event after the callback
   */
  bind: function bind(event, element, callback, callbackCallback) {
    var el = element || document;

    if (el.addEventListener) {
      el.addEventListener(event, function (event) { callback(event, callbackCallback); }, false);
    } else if (el.attachEvent) {
      el.attachEvent('on' + event, function (event) { callback(event, callbackCallback); });
    }

    log.debug('Event listener attached to event [' + event + '] to [' + (generalUtility.isWindow(element) ? 'window' : (element.id ? '#' + element.id : '.' + element.className)) + ']');
  },
  /**
   * Finds a single form with a specific attribute
   * @param {string} attribute - The attribute to search for
   * @returns {Object | undefined} - The found form or undefined if none was found
   */
  findAttributedForm: function (attribute) {
    var forms = document.getElementsByTagName('form'),
      attrForm;

    _.each(forms, function (form) {
      if (form.getAttribute(attribute) === 'form') {
        attrForm = form;
      }
    });

    return attrForm;
  },
  /**
   * Find a DOM element based on a query selector. If the element is a select box, the selected option will be returned
   * @param {string} selector - The query selector to use to search
   * @returns {Object | undefined} The DOM element if found, or undefined if nothing was found
   */
  findElementByQuery: function (selector) {
    var element = document.querySelector(selector);

    if (element && element.length) {
      switch (element.tagName) {
        case 'SELECT':
          return element.options[element.selectedIndex];
        default:
          return element[0];
      }
    } else if (element) {
      return element;
    }

    return undefined;
  },
  findElement: function (selector) {
    return document.getElementById(selector) || publicMethods.findElementByQuery('[name="' + selector + '"]');
  },
  findElementValue: function (elementSelector) {
    return publicMethods.val(elementSelector);
  },
  /**
   * Helper method to find an element given that could have many possible selectors. This function is most helpful in shopping cart integrations where field names may change based on what template is being used.
   * @param {array<string>} selectorList - An array of possible selectors for a given field we're looking for
   * @return value of element searched for or undefined if the element was not found.
   */
  findElementValueFromSelectorList: function (selectorList) {
    var elementValue;

    _.some(selectorList, function (elementSelector) {
      try {
        elementValue = publicMethods.findElementValue(publicMethods.findElement(elementSelector));
        if (elementValue) {
          return true;
        }
      } catch (e) {
        // Eat exception - Exception is thrown if the element wasn't found. We expect all but one of the give possible selectors to fail.
      }

      return false;
    });

    return elementValue;
  },
  /**
   * A small utility that will inject a form into an iframe already on the page in a cross browser manner.
   * @param {node} form - The DOM node of the form that will be injected into the iframe
   * @param {node} iframe - The DOM node of the iframe that will receive the form
   * @returns {boolean} injection was successful state
   */
  injectFormIntoIframe: function (form, iframe) {
    if (form !== undefined && iframe !== undefined) {

      var iframeDocument = iframe.contentDocument || iframe.contentWindow.document;

      // INSIGHT - In IE we may not have a body present even though the template includes a body. In this case we need to add the body manually before we can inject our form into it.
      if (iframeDocument.body === undefined || iframeDocument.body === null) {
        iframeDocument.open();
        iframeDocument.write('<body></body>');
        iframeDocument.close();
      }
      iframeDocument.body.appendChild(form);
      return true;
    } else {
      return false
    }
  },
  /**
   * A subset of utilities for generating DOM elements
   */
  factories: {
    /**
     * Creates a generic iframe dom element
     * @param {string} id - The html id of the iframe
     * @param {string=} src - The src attribute the iframe should be created with
     * @param {string | number=} height - The height of the iframe element
     * @param {string | number=} width - The width of the iframe element
     * @returns {Object} Newly created iframe DOM element
     */
    iframe: function (id, src, height, width) {
      var iframe = document.createElement('iframe');

      iframe.id = id;
      if (src !== undefined) {
        iframe.src = src;
      }
      iframe.height = height || 0;
      iframe.width = width || 0;
      iframe.border = 0;
      iframe.frameBorder = 0; // IE Fix to remove border

      return iframe;
    },
    /**
     * Creates a generic form element
     * @param {string} - url The url the form should sumbit to
     * @param {string} - method The method the form should use
     * @returns {Object} newly created form DOM element
     */
    form: function (url, method) {
      var form = document.createElement('form');

      form.action = url;
      form.method = method;

      return form;
    },
    /**
     * Creates a generic input element
     * @param {string} key - The name attribute of the input field
     * @param {string} value - The value attribute of the input field
     * @param {string} type - The type attribute of the input field
     * @returns {Object} Newly created input field DOM element
     */
    input: function (key, value, type) {
      var input = document.createElement('input');

      input.type = type;
      input.name = key;
      input.value = value;

      return input;
    },
    /**
     * Creates a generic script tag
     * @param {string=} source - The src attribute of the script tag
     * @returns {Object} Newly created script tag DOM element
     */
    script: function (source, id) {
      var script = document.createElement('script');

      script.id = id || '';
      script.src = source;
      script.type = 'text/javascript';
      script.async = true;

      return script;
    }
  },
  /**
   * Forces the browser to recalculate a specific CSS property to get the latest styles. This can be used to force the browser to render CSS changes if they're not appearing as expected. It can also be used to ensure you're dealing with the latest CSS value on an element that changes often.
   * @param {Object} element - The target DOM element that needs to have the CSS recalculate
   * @param {string} property - The specific property to recalculate
   * @returns {string} The calculated css value
   */
  getComputedStyle: function (element, property) {
    if (typeof window.getComputedStyle !== 'undefined') {
      return window.getComputedStyle(element, null).getPropertyValue(property);
    } else {
      return element.currentStyle[property];
    }
  },
  /**
   * Small utility that will inject a script element before the first script element found on the page.
   * @param {Object} script The script DOM element to inject on the page
   */
  injectScript: function (script) {
    var firstScriptOnPage = document.getElementsByTagName('script')[0];
    firstScriptOnPage.parentNode.insertBefore(script, firstScriptOnPage);
  },
  /**
   * Inject an array of scripts on the page. Each script is monitored to detect when the script is done loading and ready to use. Once all scripts within the array are loaded the returned Promise will complete. In older IE where we cannot detect when scripts have finished loading, the promise will complete immediately.
   * @param {[string]} arrayOfScripts The scripts to inject into the page
   * @returns {PromiseLike} a promise that completes when all scripts have finished loading.
   */
  injectArrayOfScripts: function (arrayOfScripts) {
    var arrayOfScriptPromises = [];

    if (!_.isArray(arrayOfScripts)) {
      throw Error("Invalid argument type, must be array");
    }

    _.each(arrayOfScripts, function (scriptSource) {
      var scriptPromise = publicMethods.injectRemoteScript(scriptSource);

      arrayOfScriptPromises.push(scriptPromise);
    });

    return RSVP.all(arrayOfScriptPromises);
  },
  /**
   * Monitor utility that will inject a script and return a Promise to inform you when it's detected as loaded.
   * @param {string} url - The url of the script to load
   * @param {string=} id - An optional html ID the script tag should use.
   * @returns {PromiseLike} a promise that fullfills or rejects based on when the script has loaded or failed.
   */
  injectRemoteScript: function (url, id) {
    // INSIGHT - IE8 does not implement the onload or onerror callbacks. Due to this we need to do some hackish checking to determine if a script downloaded successfully or not. IE8 will change the value of readyState if you try to access children elements, but only if the script tag has NOT been injected into the dom. Most other browsers require the script tag to be injected into the dom before the both onerror and onload work properly. So for IE8 we only inject the script into the dom after we know its successfully downloaded.
    return new RSVP.Promise(function (resolve, reject) {
      var script = publicMethods.factories.script(url, id),
        ie = generalUtility.ieVersion();

      // Most browser support onerror and onload
      script.onerror = function (e) {
        log.debug("3rd party script failed to load");
        reject(e);
      };

      script.onload = function () {
        log.debug("3rd party script loaded successfully");
        resolve();
      };

      try {
        // Check to see if we need to setup extra status polling for older IEs
        if (script.readyState) {

          // IE8-10 only
          script.onreadystatechange = function () {
            var state = script.readyState;

            if (/complete/.test(state)) {
              if (ie && ie < 9) {
                publicMethods.injectScript(script);
              }

              log.debug("3rd party script loaded successfully");
              resolve();
            }

            // IE8 hack: calling 'children' property changes node's readyState from 'loaded' to complete
            // (if script was loaded normally) or to 'loading' - if error detected
            if (ie && ie < 9) {
              if (state === 'loaded' && script.readyState === 'loading') {
                script.onreadystatechange = null;    // prevent mem leaks in IE
                log.debug("3rd party script failed to load");
                reject({});
              }
            }
          };
        } // implied else - not IE

        // Check to see if we need to support IE8
        if (!ie || ie > 8) {
          publicMethods.injectScript(script);
        }
      } catch (error) {
        log.error('Error while loading 3rd party script: ' + error);
      }
    });
  },
  inputListener: {
    /**
     * This function will attach an event listener to the 'oninput' event. This can be used to monitor when a fields value has changed. The action will always trigger after the debounceTime of inactivity. If content changes before the debounceTime has elapsed, the debounceTime timer will restart.
     * @param {Element} element - HTML Node to attach event to
     * @param {Function} action - The function to call when content is detected as entered
     * @param {Number} [debounceTime=500] - The cool down time in milliseconds to wait before triggering the action function.
     */
    attach: function (element, action, debounceTime) {
      var time = debounceTime;
      if (time === undefined) {
        time = 500;
      }
      if (element != null) {
        element.oninput = _.debounce(action, time, { trailing: true });
      }
    }
  },
  /**
   * This function will return the currentScript that the calling function is running in
   * @return {string} the currently running script 
   */
  getCurrentScript: function () {
    if (!('currentScript' in document)) {
      var scripts = document.getElementsByTagName('script');

      return (scripts.length > 0) ? scripts[scripts.length - 1].src : '';
    }
    return document.currentScript.src;
  }
};

module.exports = publicMethods;



//////////////////
// WEBPACK FOOTER
// ./utilities/htmlUtility.js
// module id = 67
// module chunks = 0 5 20